import React, { createContext, useContext, useState, useEffect } from 'react';
import { lightTheme, darkTheme } from '../styles/theme';
import { useAuth } from './AuthContext';
import apiClient from '../services/apiClient';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const { user, updateUser } = useAuth();
  const [isDarkMode, setIsDarkMode] = useState(() => {
    // Check localStorage first, then system preference
    const savedTheme = localStorage.getItem('bunkr-theme');
    if (savedTheme) {
      return savedTheme === 'dark';
    }
    // Check system preference
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  const [theme, setTheme] = useState(() => {
    // Initialize theme immediately based on localStorage or system preference
    const savedTheme = localStorage.getItem('bunkr-theme');
    if (savedTheme) {
      return savedTheme === 'dark' ? darkTheme : lightTheme;
    }
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? darkTheme : lightTheme;
  });
  const [isLoading, setIsLoading] = useState(false);

  // Set initial body class immediately
  useEffect(() => {
    document.body.className = isDarkMode ? 'dark-theme' : 'light-theme';
  }, []);

  // Sync with user's theme preference from backend
  useEffect(() => {
    if (user?.theme) {
      const userTheme = user.theme === 'dark';
      if (userTheme !== isDarkMode) {
        console.log('Syncing theme from user preference:', user.theme);
        setIsDarkMode(userTheme);
        // Update localStorage to match user preference
        localStorage.setItem('bunkr-theme', user.theme);
      }
    }
  }, [user?.theme]);

  useEffect(() => {
    setTheme(isDarkMode ? darkTheme : lightTheme);
    localStorage.setItem('bunkr-theme', isDarkMode ? 'dark' : 'light');
    
    // Apply theme class to body for immediate styling
    document.body.className = isDarkMode ? 'dark-theme' : 'light-theme';
  }, [isDarkMode]);

  const updateThemeOnServer = async (themeMode) => {
    if (!user || !user.id) {
      console.log('User not authenticated or missing user ID, skipping theme save to server');
      return;
    }
    
    try {
      setIsLoading(true);
      const token = localStorage.getItem('bunkr_token');
      console.log('Saving theme to server:', themeMode, 'for user:', user.id);
      console.log('Token available:', !!token);
      console.log('Token preview:', token ? token.substring(0, 20) + '...' : 'No token');
      
      const response = await apiClient.put('/auth/theme', { theme: themeMode });
      console.log('Theme updated on server successfully:', response.data);
      
      // Update the user context with the new theme preference
      if (response.data && response.data.user) {
        updateUser({ theme: themeMode });
        console.log('Updated user context with new theme:', themeMode);
        
        // Force immediate theme update to ensure UI reflects the change
        const newTheme = themeMode === 'dark' ? darkTheme : lightTheme;
        setTheme(newTheme);
        document.body.className = themeMode === 'dark' ? 'dark-theme' : 'light-theme';
        console.log('Force updated theme to:', themeMode);
      }
    } catch (error) {
      console.error('Failed to update theme on server:', error);
      console.error('Error details:', error.response?.data);
      console.error('Error status:', error.response?.status);
      console.error('Error headers:', error.response?.headers);
      // Don't revert the theme change on client side if server update fails
    } finally {
      setIsLoading(false);
    }
  };

  const toggleTheme = () => {
    const newMode = !isDarkMode;
    setIsDarkMode(newMode);
    updateThemeOnServer(newMode ? 'dark' : 'light');
  };

  const setThemeMode = (mode) => {
    const newMode = mode === 'dark';
    setIsDarkMode(newMode);
    updateThemeOnServer(mode);
  };

  const value = {
    isDarkMode,
    theme,
    toggleTheme,
    setThemeMode,
    isLoading,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};
